import React from 'react'
import {useTranslation} from 'react-i18next'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {Checkbox, Form} from 'antd'
import {usePblCommon} from '../services/'
import {
    AmountCurrencyFormField,
    IconTitle,
    Input,
    OrderNumberFormField,
    TransactionTypeFormField
} from '@/components'
import {generateInvoiceId} from '@/utils'
import {DEFAULT_CURRENCY} from '@/constants/currencies'
import {INewPaymentLinkModalStore} from '@/pages/PayByLink/components'
import {getNewPaymentLinkTransactionTypes} from '@/utils/transactions'
import {PayByLinkCreateNewLinkStoreSymbol} from '@/pages/PayByLink'
import {TerminalSelectField, ExpirationDateField} from '../../'
import {OrderNumberOptionsField} from '../OrderNumberOptionsField'
import {PaymentReferenceMode} from '@/api'
import {OneTimeFormProps} from './props'
import {
    NEW_PAYMENT_CUSTOMER_NAME,
    NEW_PAYMENT_DESCRIPTION,
    NEW_PAYMENT_RECURRING,
    SELECT_TRANSACTION_TYPE
} from '@/constants/playwright-ids'

export const OneTimeForm: React.FC<OneTimeFormProps> = observer(({form, formId, onFinish}) => {
    const store = useInjection<INewPaymentLinkModalStore>(PayByLinkCreateNewLinkStoreSymbol)
    const {isVerification, getTerminalData} = usePblCommon(form, store.terminals)
    const {t} = useTranslation()
    const paymentReferenceMode = Form.useWatch('paymentReferenceMode', form)
    const orderNumberTooltip =
        paymentReferenceMode === PaymentReferenceMode.PAYER_INPUT
            ? t('OrderNumberOptions.tooltips.oneTimeOrderNumber')
            : undefined

    const handleReloadClick = (invoiceId: string) => form.setFieldsValue({invoiceId})

    return (
        <Form
            id={formId}
            form={form}
            initialValues={{
                invoiceId: generateInvoiceId(),
                currency: DEFAULT_CURRENCY,
                paymentReferenceMode: PaymentReferenceMode.FIXED
            }}
            layout='vertical'
            labelCol={{span: 24}}
            wrapperCol={{span: 24}}
            onFinish={onFinish}
            autoComplete='off'
        >
            <TerminalSelectField options={store.terminals} />

            <OrderNumberFormField
                onReloadClick={handleReloadClick}
                tooltipText={orderNumberTooltip}
            />

            <OrderNumberOptionsField />

            <Form.Item shouldUpdate noStyle>
                {() =>
                    getTerminalData().card?.pblTransactionTypeSelection && (
                        <TransactionTypeFormField
                            data-test-id={SELECT_TRANSACTION_TYPE}
                            form={form}
                            transactionTypes={getNewPaymentLinkTransactionTypes()}
                        />
                    )
                }
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
                {() => (
                    <Form.Item
                        hidden={!getTerminalData().card?.allowPblRecurring}
                        name='recurring'
                        valuePropName='checked'
                        data-test-id={NEW_PAYMENT_RECURRING}
                    >
                        <Checkbox>
                            <IconTitle
                                title={t('OneTimeForm.labels.recurring')}
                                tooltipText={t('OneTimeForm.tooltips.recurring')}
                            />
                        </Checkbox>
                    </Form.Item>
                )}
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
                {() => (
                    <AmountCurrencyFormField
                        currencies={getTerminalData().supportedCurrencies}
                        form={form}
                        inputProps={{disabled: isVerification()}}
                        shouldAcceptZero={isVerification()}
                    />
                )}
            </Form.Item>

            <Form.Item
                label={t('OneTimeForm.labels.customerName')}
                name='customerName'
                rules={[
                    {
                        required: true,
                        message: t('OneTimeForm.errors.customerNameRequired')
                    }
                ]}
                data-test-id={NEW_PAYMENT_CUSTOMER_NAME}
            >
                <Input maxLength={45} placeholder={t('OneTimeForm.labels.name')} />
            </Form.Item>

            <Form.Item
                label={t('OneTimeForm.labels.description')}
                name='description'
                rules={[
                    {
                        required: true,
                        message: t('OneTimeForm.errors.descriptionRequired')
                    }
                ]}
                data-test-id={NEW_PAYMENT_DESCRIPTION}
            >
                <Input
                    maxLength={512}
                    placeholder={t('OneTimeForm.labels.nameOfTheServiceOrItemYouProvide')}
                />
            </Form.Item>

            <ExpirationDateField form={form} />
        </Form>
    )
})
